import { Head, Link, router, usePage } from "@inertiajs/react"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import DownloadIcon from "@mui/icons-material/Download"
import {
    Alert, Box, Button, Chip, Container, Divider, IconButton, Paper, TextField,
    Tooltip, Typography,
} from "@mui/material"
import dayjs from "dayjs"
import { ElementType, useEffect, useRef, useState } from "react"
import { Controller, SubmitHandler, useFieldArray, useForm, useWatch } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { formatErrorMessages } from "@/lib/formErrors"
import { orderFormResetKey, shouldResetOrderForm } from "@/lib/orderForm"
import ClientInfoSection from "./form_sections/ClientInfoSection"
import ExtrusionSection from "./form_sections/ExtrusionSection"
import GeneralInfoSection from "./form_sections/GeneralInfoSection"
import PrintingSection from "./form_sections/PrintingSection"
import ReferenceSection from "./form_sections/ReferenceSection"
import SealingSection from "./form_sections/SealingSection"
import { defaultOrder, FormProps, IItem, IOrder, IPhysicalCount } from "./form_sections/types"

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyData = Record<string, any>

export default function OrderForm() {
    const { is_edit, order: initialOrder, initial_order_code, initial_order_number, back_params, errors: serverErrors } =
        usePage<FormProps>().props

    const { t } = useTranslation()
    const [submitting, setSubmitting] = useState(false)
    const [localErrors, setLocalErrors] = useState<Record<string, string[]>>({})
    const [items, setItems] = useState<IItem[]>([])
    const [loadingItems, setLoadingItems] = useState(true)
    const [itemsLoadError, setItemsLoadError] = useState(false)
    const [physicalCounts, setPhysicalCounts] = useState<IPhysicalCount[]>([])
    const [loadingPhysicalCounts, setLoadingPhysicalCounts] = useState(true)
    const [physicalCountsLoadError, setPhysicalCountsLoadError] = useState(false)
    const [createPhysicalCount, setCreatePhysicalCount] = useState(false)
    const [downloadError, setDownloadError] = useState(false)

    const backQueryString = new URLSearchParams(
        Object.fromEntries(Object.entries(back_params).filter(([, v]) => v != null)) as Record<string, string>
    ).toString()
    const backUrl = `/orders?${backQueryString}`

    const defaultValues: IOrder = initialOrder
        ? { ...initialOrder, order_mix_entries: initialOrder.order_mix_entries.map(e => ({ ...e, item: null })) }
        : defaultOrder(initial_order_code ?? "", initial_order_number ?? 0)

    const { control, handleSubmit, setValue: setValueRaw, getValues, reset } = useForm<IOrder>({ defaultValues })
    const setValue = setValueRaw as (name: string, value: unknown) => void

    const { fields: refFields, append: appendRef, update: updateRef } = useFieldArray({ control, name: "order_references" })

    // Re-sync RHF state to the server payload on the initial load AND after
    // every save: without it the id=0 placeholders for rows the save just
    // created stay in the form and get created again on the next save. A 422
    // re-render is not a save, so it must not re-sync — see shouldResetOrderForm.
    const orderKey = orderFormResetKey(initialOrder)
    const hasServerErrors = Boolean(serverErrors && Object.keys(serverErrors).length > 0)
    const syncedOrderKeyRef = useRef<string | null>(null)
    useEffect(() => {
        if (!shouldResetOrderForm(syncedOrderKeyRef.current, orderKey, hasServerErrors)) return
        syncedOrderKeyRef.current = orderKey
        if (initialOrder) {
            reset({
                ...initialOrder,
                order_mix_entries: initialOrder.order_mix_entries.map(e => ({ ...e, item: null })),
            })
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orderKey, hasServerErrors])

    useEffect(() => {
        if (serverErrors) setLocalErrors(serverErrors)
    }, [])

    useEffect(() => {
        fetch("/orders/mix_items")
            .then(r => {
                if (!r.ok) throw new Error(`mix_items request failed with status ${r.status}`)
                return r.json()
            })
            .then((data: IItem[]) => setItems(data))
            .catch(err => {
                console.error(err)
                setItemsLoadError(true)
            })
            .finally(() => setLoadingItems(false))
    }, [])

    useEffect(() => {
        fetch("/orders/physical_counts")
            .then(r => {
                if (!r.ok) throw new Error(`physical_counts request failed with status ${r.status}`)
                return r.json()
            })
            .then((data: IPhysicalCount[]) => setPhysicalCounts(data))
            .catch(err => {
                console.error(err)
                setPhysicalCountsLoadError(true)
            })
            .finally(() => setLoadingPhysicalCounts(false))
    }, [])


    // ── Watchers for computed values ──────────────────────────────────────────

    const refWidth     = useWatch({ control, name: "reference_width" }) as number
    const refHeight    = useWatch({ control, name: "reference_height" }) as number
    const refGrammage  = useWatch({ control, name: "reference_grammage" }) as number
    const refSides     = useWatch({ control, name: "reference_sides" }) as number
    const refBellows   = useWatch({ control, name: "reference_bellows" }) as number
    const reqAmount    = useWatch({ control, name: "general_info_requested_amount" }) as number
    const wastePct     = useWatch({ control, name: "general_info_waste_percentage" }) as number
    const refilePct    = useWatch({ control, name: "general_info_refile_percentage" }) as number
    const weightPerRoll = useWatch({ control, name: "final_product_weight_per_roll" }) as number
    const extrusionCaliber = useWatch({ control, name: "extrusion_caliber" }) as number
    const treatment    = useWatch({ control, name: "extrusion_treatment" }) as boolean
    const progTotal    = useWatch({ control, name: "general_info_programmed_total" }) as number
    const amtPerPkg    = useWatch({ control, name: "sealing_amount_per_package" }) as number
    const pkgsPerMC    = useWatch({ control, name: "sealing_packages_per_master_case_count" }) as number
    const orderDate    = useWatch({ control, name: "order_date" }) as string
    const orderCode    = useWatch({ control, name: "order_code" }) as string

    const originalDateRef = useRef<string | null>(initialOrder?.order_date ?? null)
    const initialMountRef = useRef(true)

    useEffect(() => {
        const w = Number(refWidth), h = Number(refHeight), g = Number(refGrammage)
        const s = Number(refSides), b = Number(refBellows)
        const ra = Number(reqAmount), wp = Number(wastePct), rp = Number(refilePct)
        const wpr = Number(weightPerRoll)

        const weightMax = ((w + b) * h * g * s) / 10000
        const progKgs = (weightMax * ra) / 1000
        const waste = progKgs * (wp / 100)
        const refile = progKgs * (rp / 100)
        const total = progKgs + waste + refile

        setValue("general_info_thousand_weight_max", weightMax)
        setValue("general_info_thousand_weight_min", weightMax - 0.02)
        setValue("general_info_programmed_kilograms", progKgs)
        setValue("general_info_waste", waste)
        setValue("general_info_refile", refile)
        setValue("general_info_programmed_total", total)
        setValue("general_info_rolls_amount", wpr > 0 ? total / wpr : 0)
    }, [refWidth, refHeight, refGrammage, refSides, refBellows, reqAmount, wastePct, refilePct, weightPerRoll])

    useEffect(() => {
        const g = Number(refGrammage)
        const caliber = g / 20
        setValue("extrusion_grammage", g)
        setValue("extrusion_caliber", caliber)
        setValue("extrusion_caliber_min", caliber - 0.05)
        setValue("extrusion_caliber_max", caliber)
    }, [refGrammage])

    useEffect(() => {
        setValue("extrusion_bellows", Number(refBellows) / 2.0)
    }, [refBellows])

    useEffect(() => {
        if (!treatment) setValue("extrusion_treatment_type", 0)
    }, [treatment])

    useEffect(() => {
        const w = Number(refWidth), h = Number(refHeight)
        setValue("sealing_width", w)
        setValue("sealing_width_min", w)
        setValue("sealing_width_max", w + 0.02)
        setValue("sealing_length", h)
        setValue("sealing_length_min", h)
        setValue("sealing_length_max", h + 0.02)
    }, [refWidth, refHeight])

    useEffect(() => {
        const amc = Number(amtPerPkg) * Number(pkgsPerMC)
        setValue("sealing_amount_per_master_case", amc)
        setValue("sealing_master_case_count", amc > 0 ? Number(reqAmount) / amc : 0)
    }, [amtPerPkg, pkgsPerMC, reqAmount])

    useEffect(() => {
        const total = Number(progTotal), wpr = Number(weightPerRoll)
        const rolls = wpr > 0 ? total / wpr : 0
        const mtPerRoll = (Number(refWidth) * Number(extrusionCaliber) * 0.004) > 0
            ? ((rolls * 1000) / (Number(refWidth) * Number(extrusionCaliber) * 0.004)) / 100
            : 0
        setValue("printing_rolls_number", rolls)
        setValue("printing_kg_per_roll", wpr)
        setValue("printing_mt_per_roll", mtPerRoll)
    }, [progTotal, weightPerRoll, refWidth, extrusionCaliber])

    useEffect(() => {
        if (initialMountRef.current) { initialMountRef.current = false; return }
        if (!orderDate) return

        const d = dayjs(orderDate)
        if (!d.isValid()) return

        if (is_edit && originalDateRef.current) {
            const orig = dayjs(originalDateRef.current)
            if (orig.month() === d.month() && orig.year() === d.year()) {
                setValue("order_code", initialOrder!.order_code)
                setValue("order_number", initialOrder!.order_number)
                return
            }
        }

        fetch(`/orders/new_order_code?date=${d.format("YYYY-MM-DD")}`)
            .then(r => r.json())
            .then(data => {
                setValue("order_code", data.order_code)
                setValue("order_number", data.order_number)
            })
            .catch(() => {})
    }, [orderDate])

    // ── Submit handlers ───────────────────────────────────────────────────────

    const buildOnError = (originalUrl: string) =>
        (errors: Record<string, string | string[]>) => {
            window.history.replaceState(window.history.state, "", originalUrl)
            setLocalErrors(Object.fromEntries(
                Object.entries(errors).map(([k, v]) => [k, Array.isArray(v) ? v : [v]])
            ))
            setSubmitting(false)
        }
    const onSuccess = () => setSubmitting(false)

    const handleSave: SubmitHandler<IOrder> = (data) => {
        setSubmitting(true)
        setLocalErrors({})
        const originalUrl = window.location.pathname + window.location.search
        const url = is_edit ? `/orders/${initialOrder!.id}?${backQueryString}` : `/orders?${backQueryString}`
        router[is_edit ? "patch" : "post"](url, {
            order: data as AnyData,
            create_physical_count: createPhysicalCount,
        }, {
            onError: buildOnError(originalUrl),
            onSuccess: is_edit ? undefined : () => { sessionStorage.removeItem('scroll_orders') },
            onFinish: onSuccess,
        })
    }

    const handleSaveAndContinue: SubmitHandler<IOrder> = (data) => {
        setSubmitting(true)
        setLocalErrors({})
        const originalUrl = window.location.pathname + window.location.search
        const url = is_edit ? `/orders/${initialOrder!.id}?${backQueryString}` : `/orders?${backQueryString}`
        router[is_edit ? "patch" : "post"](url, {
            order: data as AnyData,
            create_physical_count: createPhysicalCount,
            stay: true,
        }, {
            preserveScroll: true,
            onError: buildOnError(originalUrl),
            onFinish: onSuccess,
        })
    }

    const handleDownload = async () => {
        if (is_edit && initialOrder?.id) {
            window.location.href = `/orders/${initialOrder.id}/download_excel`
            return
        }
        const data = getValues()
        const csrf = (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content ?? ""
        setSubmitting(true)
        setDownloadError(false)
        try {
            const res = await fetch("/orders/preview_excel", {
                method: "POST",
                headers: { "Content-Type": "application/json", "X-CSRF-Token": csrf },
                body: JSON.stringify({ order: data }),
            })
            if (!res.ok) {
                setDownloadError(true)
                return
            }
            const blob = await res.blob()
            const url = URL.createObjectURL(blob)
            const a = document.createElement("a")
            a.href = url
            a.download = `${data.order_code || "order"}.xlsx`
            a.click()
            URL.revokeObjectURL(url)
        } catch {
            setDownloadError(true)
        } finally {
            setSubmitting(false)
        }
    }

    const errorMessages = formatErrorMessages(t, localErrors)

    const title = is_edit ? (orderCode || t("orders.form.newOrder")) : t("orders.form.newOrder")

    // ── Render ────────────────────────────────────────────────────────────────

    return (
        <Box sx={{ pb: 8 }}>
            <Head title={title} />

            <Box sx={{ bgcolor: "background.paper", borderBottom: 1, borderColor: "divider" }}>
                <Container maxWidth="xl">
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 1 }}>
                        <Tooltip title={t("common.back")}>
                            <IconButton component={Link as ElementType} href={backUrl} size="small">
                                <ArrowBackIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Divider orientation="vertical" flexItem sx={{ display: { xs: "none", sm: "block" } }} />
                        <Typography
                            variant="subtitle1"
                            noWrap
                            sx={{ fontWeight: "bold", flexGrow: 1, display: { xs: "none", sm: "block" } }}
                        >
                            {title}
                        </Typography>
                        <Box sx={{ flexGrow: 1, display: { xs: "block", sm: "none" } }} />

                        <Tooltip title={t("common.downloadExcel")}>
                            <span>
                                <IconButton
                                    color="success"
                                    size="small"
                                    onClick={handleDownload}
                                    disabled={submitting || loadingItems}
                                    sx={{ display: { xs: "inline-flex", sm: "none" } }}
                                >
                                    <DownloadIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Button
                            variant="outlined"
                            color="success"
                            size="small"
                            startIcon={<DownloadIcon />}
                            onClick={handleDownload}
                            disabled={submitting || loadingItems}
                            sx={{ display: { xs: "none", sm: "inline-flex" } }}
                        >
                            {t("common.excel")}
                        </Button>

                        <Button color="inherit" size="small" component={Link as ElementType} href={backUrl} disabled={submitting || loadingItems}>
                            {t("common.cancel")}
                        </Button>
                        <Button variant="outlined" size="small" onClick={handleSubmit(handleSaveAndContinue)} disabled={submitting || loadingItems}>
                            {t("common.saveAndContinue")}
                        </Button>
                        <Button variant="contained" size="small" onClick={handleSubmit(handleSave)} disabled={submitting || loadingItems}>
                            {submitting ? t("common.saving") : t("common.save")}
                        </Button>
                    </Box>
                </Container>
            </Box>

            <Container maxWidth="xl" sx={{ py: 3 }}>
                {errorMessages.length > 0 && (
                    <Alert severity="error" sx={{ mb: 3 }}>{errorMessages.join(" · ")}</Alert>
                )}

                {downloadError && (
                    <Alert severity="error" sx={{ mb: 3 }} onClose={() => setDownloadError(false)}>
                        {t("common.downloadError")}
                    </Alert>
                )}

                {itemsLoadError && (
                    <Alert severity="warning" sx={{ mb: 3 }}>{t("orders.form.itemsLoadError")}</Alert>
                )}

                {physicalCountsLoadError && (
                    <Alert severity="warning" sx={{ mb: 3 }}>{t("orders.form.physicalCountsLoadError")}</Alert>
                )}

                {initialOrder?.export && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                            {t("orders.inExport")}:
                        </Typography>
                        <Tooltip title={t("orders.openExport")}>
                            <Chip
                                label={initialOrder.export.name}
                                size="small"
                                color="success"
                                variant="outlined"
                                component="a"
                                href={`/exports/${initialOrder.export.id}/edit`}
                                target="_blank"
                                clickable
                            />
                        </Tooltip>
                    </Box>
                )}

                <ClientInfoSection
                    control={control} getValues={getValues}
                    refFields={refFields} appendRef={appendRef} updateRef={updateRef}
                    physicalCounts={physicalCounts} loadingPhysicalCounts={loadingPhysicalCounts}
                    createPhysicalCount={createPhysicalCount}
                    onCreatePhysicalCountChange={setCreatePhysicalCount}
                />
                <ReferenceSection control={control} setValue={setValue} />
                <GeneralInfoSection control={control} />
                <ExtrusionSection
                    control={control} setValue={setValue} getValues={getValues}
                    treatment={treatment} items={items} loadingItems={loadingItems}
                />
                <SealingSection control={control} />
                <PrintingSection control={control} />

                <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1 }}>{t("orders.form.comment")}</Typography>
                    <Controller control={control} name="order_comment"
                        render={({ field }) => (
                            <TextField {...field} multiline rows={4} fullWidth size="small" />
                        )}
                    />
                </Paper>
            </Container>
        </Box>
    )
}
